import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { device, ROUTES } from '../../../../constants';
import { deviceHeight } from '../../../../constants/mediaQueries';
import { Link } from 'react-router-dom';
import { MotionHomeIndustriesImage } from '../../../../components/Motion';

interface IndustriesSliderProps {
  domains: { image: string; name: string; projectName: string }[];
  currentIndex: number;
  changeIndex: (index: number) => void;
}

export const IndustriesSlider: React.FC<IndustriesSliderProps> = ({
  domains,
  currentIndex,
  changeIndex,
}) => {
  const [loadingState, setLoadingState] = useState({
    prev: true,
    curr: true,
    next: true,
  });
  const prevIndex = (currentIndex - 1 + domains.length) % domains.length;
  const nextIndex = (currentIndex + 1) % domains.length;
  useEffect(() => {
    setLoadingState({ prev: true, curr: true, next: true });
    const imgPrev = new Image();
    const imgCurr = new Image();
    const imgNext = new Image();
    imgPrev.src = domains[prevIndex].image;
    imgCurr.src = domains[currentIndex].image;
    imgNext.src = domains[nextIndex].image;
    const images = [imgPrev, imgCurr, imgNext];
    images.forEach((img, index) => {
      if (img.complete) {
        setLoadingState(prevState => ({
          ...prevState,
          [index === 0 ? 'prev' : index === 1 ? 'curr' : 'next']: false,
        }));
      } else {
        img.onload = () =>
          setLoadingState(prevState => ({
            ...prevState,
            [index === 0 ? 'prev' : index === 1 ? 'curr' : 'next']: false,
          }));
      }
    });
  }, [currentIndex, domains, prevIndex, nextIndex]);

  return (
    <SliderContainer>
      <OtherImageContainer
        onClick={() => changeIndex(prevIndex)}
        currentIndex={currentIndex}
      >
        {loadingState.prev && <Skeleton />}
        <img
          draggable="false"
          src={loadingState.prev ? '' : domains[prevIndex].image}
          alt={domains[prevIndex].name}
          style={{ display: loadingState.prev ? 'none' : 'block' }}
        />
      </OtherImageContainer>
      <ImageContainer currentIndex={currentIndex}>
        <Link
          to={`${ROUTES.PROJECT}/${domains[currentIndex].projectName.replace(/\s+/g, '_')}`}
        >
          {loadingState.curr && <Skeleton />}
          <img
            draggable="false"
            src={loadingState.curr ? '' : domains[currentIndex].image}
            alt={domains[currentIndex].name}
            style={{ display: loadingState.curr ? 'none' : 'block' }}
          />
        </Link>
      </ImageContainer>
      <OtherImageContainer
        onClick={() => changeIndex(nextIndex)}
        currentIndex={currentIndex}
      >
        {loadingState.next && <Skeleton />}
        <img
          draggable="false"
          src={loadingState.next ? '' : domains[nextIndex].image}
          alt={domains[nextIndex].name}
          style={{ display: loadingState.next ? 'none' : 'block' }}
        />
      </OtherImageContainer>
    </SliderContainer>
  );
};

const SliderContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  max-width: 500px;
  height: 700px;
  overflow: hidden;
  gap: 30px;
  @media screen and ${device.lg} {
    width: 100%;
  }
  @media screen and ${deviceHeight.md} and ${device.xl} {
    max-height: 530px;
  }
  @media screen and ${device.bsm} {
    order: 1;
    flex-direction: row;
    justify-content: center;
    max-width: 80%;
  }
  @media screen and ${device.sm} {
    max-height: 400px;
  }
`;

const OtherImageContainer = styled(MotionHomeIndustriesImage)`
  height: 480px;
  max-width: 500px;
  flex-shrink: 0;
  position: relative;
  display: flex;
  &:nth-child(1) {
    align-items: flex-end;
  }
  &:nth-child(3) {
    align-items: flex-start;
  }
  img {
    width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  @media screen and ${deviceHeight.md} and ${device.xl} {
    height: 300px;
    max-width: 350px;
  }
  @media screen and ${device.bsm} {
    height: 400px;
    width: 250px;
    opacity: 0.7;
    &:nth-child(1),
    &:nth-child(3) {
      align-items: center;
    }
  }
  @media screen and ${device.sm} {
    max-height: 250px;
  }
`;

const ImageContainer = styled(MotionHomeIndustriesImage)`
  height: 480px;
  max-width: 500px;
  flex-shrink: 0;
  position: relative;
  user-select: none;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  @media screen and ${deviceHeight.md} and ${device.xl} {
    height: 300px;
    max-width: 350px;
  }
  @media screen and ${device.bsm} {
    height: 500px;
    max-width: 350px;
  }
  @media screen and ${device.sm} {
    height: 350px;
  }
`;

const Skeleton = styled.div`
  height: 100%;
  width: 400px;
  background-color: #000;
  animation: skeleton-loading 1.2s infinite;
  border-radius: 4px;
  @media screen and ${deviceHeight.md} and ${device.xl} {
    max-width: 250px;
  }
  @keyframes skeleton-loading {
    0% {
      background-color: #e0e0e0;
    }
    50% {
      background-color: #f0f0f0;
    }
    100% {
      background-color: #e0e0e0;
    }
  }
`;
